import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Link, SocialNetworksAPI, TextAPI } from '@philips-emr/tws-components';
import { DismissStyle, ThemeTonalRange, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnCatch, OnClick, OnMount, OnSuccess } from '@philips-emr/tws-core/lib/client';
import { MountEvent, PromiseEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService, ThemeService, ToastService as ToastServiceFw } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';
import { LoginAPI } from '@philips-emr/tws-foundation';
import { getBreakpoint } from '@philips-emr/tws-utils';

import { FragmentNames, LoginAliases } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { Configuration } from '../../services/configuration';

@Controller({ fragment: FragmentNames.LOGIN })
export default class LoginController {

  @Inject(LoginAliases.LOGIN)
  private login: LoginAPI;

  @OnMount(LoginAliases.LAYOUT_LOGIN)
  onMount = (): void => ThemeService.setContentTonalRange(ThemeTonalRange.BRIGHT);

  @OnMount(LoginAliases.LOGIN)
  onLoginMount({ target }: MountEvent): void {
    void Configuration.getData().then(data => {
      data.dsUrlLogoHeaderLogin && (target as LoginAPI).setLogo(data.dsUrlLogoHeaderLogin);
    });
    setTimeout(() => this.showCookiesWarning(), 1000);
  }

  @OnMount(LoginAliases.SOCIAL_NETWORKS)
  onSocialNetworksMount({ target }: MountEvent): void {
    const api = target as SocialNetworksAPI;

    void Configuration.getData().then(data => {
      data.dsLinkFacebook && api.setFacebook(data.dsLinkFacebook);
      data.dsLinkInstagram && api.setInstagram(data.dsLinkInstagram);
      data.dsLinkLinkedin && api.setLinkedin(data.dsLinkLinkedin);
      data.dsLinkTwitter && api.setTwitter(data.dsLinkTwitter);
      data.dsLinkYoutube && api.setYoutube(data.dsLinkYoutube);
    });
  }

  @OnMount(LoginAliases.TEXT_ENDERECO)
  onEnderecoMount = ({ target }: MountEvent): void => void Configuration.getData().then(data => data.dsEndereco && (target as TextAPI).setValue(data.dsEndereco));

  @OnMount(LoginAliases.TEXT_FONE)
  onFoneMount = ({ target }: MountEvent): void => void Configuration.getData()
    .then(data => data.nrFoneContato && (target as TextAPI).setValue(`${MessageService.translate('beneficiary.telefone')}: ${data.nrFoneContato}`));

  @OnMount(LoginAliases.TEXT_MAIL)
  onMailMount = ({ target }: MountEvent): void => void Configuration.getData()
    .then(data => data.dsEmailRodape && (target as TextAPI).setValue(`${MessageService.translate('beneficiary.email')}: ${data.dsEmailRodape}`));

  @OnClick(LoginAliases.BUTTON_NOVO_USUARIO)
  onClick(): void {
    ModalService.openDefault({
      closeable: true,
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'beneficiary.cadastrar-usuario',
      fragment: FragmentNames.MODAL_CADASTRAR_NOVO_USUARIO,
    });
  }

  @OnSuccess(LoginAliases.LOGIN)
  onSuccess = (): void => void HttpService.post(Endpoints.POST.NOTIFICACAO_ATUALIZAR_CADASTRO);

  @OnCatch(LoginAliases.LOGIN)
  onCatch(event: PromiseEvent<Record<string, unknown>>): void {
    this.login.removeNotification();

    const response = event.response.response as Response;
    void response.json().then(resp => {
      const primeiraExp = resp[Object.keys(resp)[0]];
      const segundaExp = resp[Object.keys(resp)[1]];

      if (primeiraExp && !segundaExp) {
        if (resp[870103]) {
          const { username } = this.login.state;
          ModalService.openDefault({
            id: FragmentNames.MODAL_REDEFINIR_SENHA,
            closeable: true,
            theme: ThemeTonalRange.ULTRA_LIGHT,
            title: 'beneficiary.redefina-senha',
            fragment: FragmentNames.MODAL_REDEFINIR_SENHA,
            contextData: { username },
            onClose: () => this.login.setState({ password: '' }),
          });
        } else {
          this.showNotification(primeiraExp);
        }
      } else if (primeiraExp && segundaExp) {
        this.showNotification(`${primeiraExp} ${segundaExp}`);
      } else {
        this.showNotification('beneficiary.general-error');
      }
    }).catch(() => this.showNotification('beneficiary.general-error'));
  }

  private getMessageCookiePolicies(): ReactElement {
    return (
      <Link onClick={() => this.showCookiesModal()} value={MessageService.translate('beneficiary.cookies-policy')} />
    );
  }

  showCookiesModal(): void {
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: 'cookie-policies',
      title: 'beneficiary.cookies-policy',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    };
    const breakpoints = getBreakpoint() as string;
    if (breakpoints == 'sm' || breakpoints == 'xsm') {
      ModalService.openFullscreen(modalWithFragment);
    } else {
      ModalService.openDialog(modalWithFragment);
    }
  }

  private showCookiesWarning(): void {
    ToastServiceFw.open({
      id: 'c-toast-notification__message--cookies',
      title: MessageService.translate('beneficiary.cookies-policy'),
      message: MessageService.translate('beneficiary.message-cookie-policies'),
      dismissible: true,
      dismissStyle: DismissStyle.BUTTON,
      showIcon: true,
      dismissExpression: MessageService.translate('beneficiary.ok'),
      action: this.getMessageCookiePolicies(),
      level: ToastLevel.WARNING,
      highlight: true,
    });
  }

  private showNotification(message: string): void {
    this.login.setNotification({
      highlight: true,
      level: ToastLevel.WARNING,
      message: MessageService.translate(message),
    });
  }

}
